import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { featureFlag, FeatureFlagPipe, FeatureService } from '@sb/shared/feature';
import { ButtonComponent } from '@sb/ui/button';
import { CheckboxComponent } from '@sb/ui/checkbox';
import { DIALOG_DATA, DialogRef, SbDialogModule } from '@sb/ui/dialog';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { InputDirective } from '@sb/ui/input';
import { LaddaDirective } from '@sb/ui/ladda';
import { SelectComponent, SelectItem } from '@sb/ui/select';
import { finalize } from 'rxjs';

import { TemplateValidationResult } from '../../models/contract-template.model';
import { ContractTemplateService } from '../../services/contract-template.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'edit-contract-template-modal',
  templateUrl: './edit-contract-template-modal.component.html',
  imports: [
    TranslatePipe,
    ButtonComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    SbFormFieldComponent,
    SbDialogModule,
    LaddaDirective,
    InputDirective,
    SelectComponent,
    FeatureFlagPipe,
  ],
})
export class EditTemplateContractModalComponent {
  private destroyRef = inject(DestroyRef);
  private dialogRef = inject(DialogRef);
  private contractTemplateService = inject(ContractTemplateService);
  #translate = inject(TranslateService);
  data = inject<{
    contractInfo: TemplateValidationResult;
  }>(DIALOG_DATA);

  features = featureFlag;
  loading = false;
  form = new FormGroup({
    title: new FormControl(this.data.contractInfo.title, [Validators.required, noWhitespaceValidator]),
    // The legacy single-guardian checkbox (count flag off) and the count selector (count flag on)
    // each bind their own control; both initial values derive from the persisted count, with a
    // back-compat fallback to the legacy requiresGuardianSignature when a read path omits the count.
    requiresGuardianSignature: new FormControl<boolean>(this.#initialGuardianCount() > 0, { nonNullable: true }),
    guardianSignatureCount: new FormControl<number>(this.#initialGuardianCount(), { nonNullable: true }),
  });

  // Editing can't add placeholders to the already-uploaded document, and the backend
  // re-asserts the count against the extracted placeholders (a higher count 400s). So only
  // offer counts the document's guardian placeholders can satisfy; the rest are disabled.
  #maxGuardianCount = this.#computeMaxGuardianCount();
  #countFeatureEnabled = inject(FeatureService).isEnabled(featureFlag.TMP_GUARDIAN_SIGNATURE_COUNT);

  // The selector only renders when TMP_GUARDIAN_SIGNATURE_COUNT is on (otherwise the legacy
  // single-guardian checkbox is shown), so always offer 0/1/2 — disabling counts the document's
  // guardian placeholders can't satisfy.
  guardianSignatureCountItems: SelectItem<number>[] = [
    { value: 0, text: this.#translate.instant('No guardians') },
    { value: 1, text: this.#translate.instant('1 guardian'), disabled: this.#maxGuardianCount < 1 },
    { value: 2, text: this.#translate.instant('2 guardians'), disabled: this.#maxGuardianCount < 2 },
  ];

  #initialGuardianCount(): number {
    const initialCount =
      this.data.contractInfo.guardianSignatureCount ?? (this.data.contractInfo.requiresGuardianSignature ? 1 : 0);

    // Don't initialize to a count the uploaded document can't satisfy (its option is disabled and
    // saving would 400); clamp to what the extracted guardian placeholders support.
    return Math.min(initialCount, this.#computeMaxGuardianCount());
  }

  #computeMaxGuardianCount(): number {
    const names = new Set((this.data.contractInfo.variables ?? []).map((variable) => variable.variable));

    if (names.has('signature_guardian_2') && names.has('signing_date_guardian_2')) {
      return 2;
    }

    if (names.has('signature_guardian') && names.has('signing_date_guardian')) {
      return 1;
    }

    return 0;
  }

  close() {
    this.dialogRef.close();
  }

  submit() {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    this.loading = true;

    const { title, requiresGuardianSignature, guardianSignatureCount } = this.form.getRawValue();
    // Count flag on → the selector's 0/1/2; off → the legacy checkbox maps to 0 or 1.
    const count = this.#countFeatureEnabled ? guardianSignatureCount : requiresGuardianSignature ? 1 : 0;
    void this.contractTemplateService
      .updateTemplateContractInfo(this.data?.contractInfo.id, {
        title,
        guardianSignatureCount: count,
        // Back-compat during rollout: also send the legacy boolean derived from the count so a backend
        // that hasn't picked up guardianSignatureCount yet still records the requirement. Drop with SHB-20703.
        requiresGuardianSignature: count > 0,
      })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.close();
      });
  }
}

function noWhitespaceValidator(control: FormControl) {
  const isWhitespace = (control.value || '').trim().length === 0;
  return isWhitespace ? { whitespace: true } : null;
}
